import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Button } from 'antd';
import { MaterialReactTable, MRT_TablePagination } from 'material-react-table';
import { useState } from 'react';
import './GenericGrid.css';
import { Box } from '@mui/material';

interface GenericGridProps {
  columns: any[];
  data: any;
  enableRowSelection?: boolean;
  selectAllMode?: 'all' | 'page';
  onRowSelectionChange?: any;
  rowSelection?: any;
  setRowSelection?: any;
  columnVisibility?: any;
  columnPinning?: any;
  filterDisplayMode?: any;
  highlightFilterMatch?: boolean;
  paginationDisplayMode?: any;
  paginationProps?: any;
  pagination?: any;
  sorting?: any;
  columnOrder?: string[];
  enableStickyHeader?: boolean;
  enableStickyFooter?: boolean;
  enableColumnPinning?: boolean;
  enableColumnOrdering?: boolean;
  isLoading?: boolean;
  muiTableBodyCellProps?: any;
  muiTableHeadCellProps?: any;
  muiTableBodyRowProps?: any;
  enableEditing?: any;
  onSaveChanges?: any;
  enablePagination?: boolean;
  filteredData?: any;
  setFilteredData?: any;
  renderToolbarInternalActions?: any;
  renderTopToolbarCustomActions?: (props: { table: any }) => React.ReactNode;
  renderToolbarAlertBannerContent?: (props: { table: any }) => React.ReactNode;
  enableGlobalFilter?: boolean;
  hideTotalRows?: boolean;
  enableBottomToolbar?: boolean;
  enableTopToolbar?: boolean;
  addDrawerComponent?: React.ReactNode;
  enableAdd?: boolean;
  totalRecordsLabel?: string;
  onAddClick?: () => void;
  onColumnFiltersChange?: (filters: any[]) => void;
  onSortingChange?: (sorting: any[]) => void;
}

const GenericGrid: React.FC<GenericGridProps> = ({
  columns,
  data,
  enableRowSelection,
  selectAllMode,
  onRowSelectionChange,
  rowSelection,
  setRowSelection,
  columnVisibility,
  columnPinning,
  filterDisplayMode,
  highlightFilterMatch,
  paginationDisplayMode,
  paginationProps,
  pagination,
  sorting,
  columnOrder,
  enableStickyHeader,
  enableStickyFooter,
  enableColumnPinning,
  enableColumnOrdering,
  isLoading,
  muiTableBodyCellProps,
  muiTableHeadCellProps,
  muiTableBodyRowProps,
  enableEditing,
  onSaveChanges,
  enablePagination,
  filteredData,
  setFilteredData,
  renderToolbarInternalActions,
  renderTopToolbarCustomActions,
  renderToolbarAlertBannerContent,
  enableGlobalFilter,
  hideTotalRows,
  enableBottomToolbar,
  enableTopToolbar,
  enableAdd,
  onAddClick,
  totalRecordsLabel,
  onColumnFiltersChange,
  onSortingChange,
}: GenericGridProps) => {
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sortingState, setSortingState] = useState<any>(sorting || []);

  const handleColumnFiltersChange = (updaterOrValue: any) => {
    const newFilters = typeof updaterOrValue === 'function' ? updaterOrValue(columnFilters) : updaterOrValue;
    setColumnFilters(newFilters);
    if (onColumnFiltersChange) {
      onColumnFiltersChange(newFilters);
    }
  };

  const handleSortingChange = (updaterOrValue: any) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sortingState) : updaterOrValue;
    setSortingState(newSorting);
    if (onSortingChange) {
      onSortingChange(newSorting);
    }
  };
  const [columnVisibile, setColumnVisibility] = useState(columnVisibility);
  const handleColumnVisibilityChange = (updatedColumnVisibility: any) => {
    setColumnVisibility(updatedColumnVisibility);
  };

  const [columnPin, setColumnPinning] = useState(columnPinning);
  const handleColumnPinningChange = (newPinning: any) => {
    console.log('Column pinning changed:', newPinning);
    setColumnPinning(newPinning);
  };

  const updateFilteredData = (table: any) => {
    if (filteredData && setFilteredData) {
      const filteredRows = table.getPrePaginationRowModel().rows;
      setFilteredData(filteredRows);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className='table-container'>
        <div className='table-wrapper'>
          <MaterialReactTable
            columns={columns}
            data={data}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            enableRowSelection={enableRowSelection}
            selectAllMode={selectAllMode}
            onRowSelectionChange={setRowSelection ?? onRowSelectionChange}
            columnFilterDisplayMode={filterDisplayMode}
            enableFilterMatchHighlighting={highlightFilterMatch}
            onColumnFiltersChange={handleColumnFiltersChange}
            onSortingChange={handleSortingChange}
            globalFilterFn='contains'
            enableGlobalFilter={enableGlobalFilter}
            paginationDisplayMode={paginationDisplayMode}
            muiPaginationProps={paginationProps}
            enableStickyHeader={enableStickyHeader}
            enableStickyFooter={enableStickyFooter}
            enableColumnPinning={enableColumnPinning}
            enablePagination={enablePagination}
            onColumnPinningChange={handleColumnPinningChange}
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableRowVirtualization={true}
            enableColumnVirtualization={true}
            enableColumnOrdering={enableColumnOrdering}
            renderToolbarInternalActions={renderToolbarInternalActions}
            renderTopToolbarCustomActions={renderTopToolbarCustomActions}
            renderToolbarAlertBannerContent={renderToolbarAlertBannerContent}
            muiCircularProgressProps={{
              color: 'secondary',
              thickness: 5,
              size: 55,
            }}
            onEditingRowSave={onSaveChanges}
            enableEditing={enableEditing}
            enableCellActions={true}
            enableRowActions={false}
            enableBottomToolbar={enableBottomToolbar}
            enableTopToolbar={enableTopToolbar}
            getRowId={(row) => row.id}
            editDisplayMode={'cell'}
            muiTableBodyCellProps={muiTableBodyCellProps}
            muiTableHeadCellProps={muiTableHeadCellProps}
            muiTableBodyRowProps={muiTableBodyRowProps}
            initialState={{
              pagination: pagination
                ? pagination
                : {
                    pageIndex: 0,
                    pageSize: 10,
                  },
              sorting: sorting ? sorting : [],
              columnOrder: columnOrder || [],
            }}
            state={{
              rowSelection,
              columnFilters,
              sorting: sortingState,
              isLoading,
              columnVisibility: columnVisibile,
              columnPinning: columnPin,
            }}
            renderBottomToolbar={({ table }) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 16px',
                }}
              >
                {/* --- Left Side Content --- */}
                {!hideTotalRows && (
                  <div className='total-div'>
                    {totalRecordsLabel || 'TOTAL RECORDS'}
                    <span className='total-count'>{table.getFilteredRowModel().rows?.length}</span>
                  </div>
                )}

                {/* --- Right Side Content: Pagination and Logo --- */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px', // Adds space between the pagination and the logo
                  }}
                >
                  <MRT_TablePagination table={table} />
                  <img alt='logo' src={'./images/logo.svg'} />
                </Box>
              </Box>
            )}
          ></MaterialReactTable>

          {enableAdd && (
            <Button className='add-btn' onClick={onAddClick}>
              <img src={'./icons/action-add.svg'} alt='add' />
            </Button>
          )}
        </div>

        {/* <img alt='logo' src={'./images/logo.svg'} /> */}
      </div>
    </LocalizationProvider>
  );
};

export default GenericGrid;
